/**
 * PHASE 12: Local Observability / Metrics
 * Instrumentation helpers for offline sync engine
 */

// In-memory metrics store
const metrics = {
  queued: {},
  replayed: {},
  failed: {},
  conflicts: {},
  collapsedCount: 0
};

const isDevelopment = typeof process !== 'undefined' && process.env?.NODE_ENV === 'development';

/**
 * Log metric event (development only)
 */
function logMetric(event, data) {
  if (isDevelopment) {
    console.log(`[OfflineMetrics] ${event}`, data);
  }
}

/**
 * Record queue enqueue event
 * @param {string} type - Action type
 * @param {boolean} collapsed - Whether it was collapsed into existing
 */
export function recordQueueEnqueued(type, collapsed = false) {
  metrics.queued[type] = (metrics.queued[type] || 0) + 1;
  if (collapsed) {
    metrics.collapsedCount++;
  }
  
  logMetric('QUEUE_ENQUEUED', { type, collapsed, total: metrics.queued[type] });
  
  // Future: send to analytics
  // analytics.track('offline_queue_enqueued', { type, collapsed });
}

/**
 * Record replay started event
 * @param {number} count - Number of items being replayed
 */
export function recordQueueReplayStarted(count) {
  logMetric('REPLAY_STARTED', { count });
}

/**
 * Record successful replay
 * @param {string} type - Action type
 */
export function recordQueueReplayCompleted(type) {
  metrics.replayed[type] = (metrics.replayed[type] || 0) + 1;
  
  logMetric('REPLAY_COMPLETED', { type, total: metrics.replayed[type] });
}

/**
 * Record failed replay
 * @param {string} type - Action type
 */
export function recordQueueReplayFailed(type) {
  metrics.failed[type] = (metrics.failed[type] || 0) + 1;
  
  logMetric('REPLAY_FAILED', { type, total: metrics.failed[type] });
}

/**
 * Record conflict detected
 * @param {string} type - Action type
 */
export function recordConflictDetected(type) {
  metrics.conflicts[type] = (metrics.conflicts[type] || 0) + 1;
  
  logMetric('CONFLICT_DETECTED', { type, total: metrics.conflicts[type] });
}

/**
 * Record network state change
 * @param {boolean} online
 */
export function recordNetworkStateChange(online) {
  logMetric('NETWORK_STATE_CHANGE', { online, timestamp: Date.now() });
}

/**
 * Record background sync event
 * @param {string} tag - Sync tag
 * @param {'started'|'completed'|'failed'} status
 */
export function recordBackgroundSync(tag, status) {
  logMetric('BACKGROUND_SYNC', { tag, status, timestamp: Date.now() });
}

/**
 * Get current metrics snapshot
 * @returns {Object}
 */
export function getMetricsSnapshot() {
  return {
    ...metrics,
    timestamp: Date.now()
  };
}

/**
 * Reset metrics (for testing)
 */
export function resetMetrics() {
  metrics.queued = {};
  metrics.replayed = {};
  metrics.failed = {};
  metrics.conflicts = {};
  metrics.collapsedCount = 0;
}

/**
 * Get summary stats
 */
export function getMetricsSummary() {
  const totalQueued = Object.values(metrics.queued).reduce((a, b) => a + b, 0);
  const totalReplayed = Object.values(metrics.replayed).reduce((a, b) => a + b, 0);
  const totalFailed = Object.values(metrics.failed).reduce((a, b) => a + b, 0);
  const totalConflicts = Object.values(metrics.conflicts).reduce((a, b) => a + b, 0);
  
  return {
    totalQueued,
    totalReplayed,
    totalFailed,
    totalConflicts,
    collapsedCount: metrics.collapsedCount,
    successRate: totalQueued > 0 ? (totalReplayed / totalQueued * 100).toFixed(1) + '%' : 'N/A'
  };
}

export default {
  recordQueueEnqueued,
  recordQueueReplayStarted,
  recordQueueReplayCompleted,
  recordQueueReplayFailed,
  recordConflictDetected,
  recordNetworkStateChange,
  recordBackgroundSync,
  getMetricsSnapshot,
  getMetricsSummary,
  resetMetrics
};
